import React, { useState } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Divider,
  Flex,
  Center,
  Spinner,
  Image as ChakraImage,
  useColorModeValue,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Utils/AuthContext';
import { useAppToast } from '../Components/Toast';
import ThemeToggle from '../Components/ThemeToggle';
import { useCompany } from '../Utils/CompanyContext';
import TenantAccessDeniedModal from '../Components/TenantAccessDeniedModal';

/* ------------------------------------------------------------------ */
/*  Constants & animations                                             */
/* ------------------------------------------------------------------ */

const NAVY = '#0D1F4C';
const NAVY_LIGHT = '#162D6B';

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-12px) rotate(1deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ------------------------------------------------------------------ */
/*  Inline SVGs                                                        */
/* ------------------------------------------------------------------ */

const MicrosoftIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }}>
    <rect x="0" y="0" width="8.2" height="8.2" fill="#f25022" />
    <rect x="9" y="0" width="8.2" height="8.2" fill="#7fba00" />
    <rect x="0" y="9" width="8.2" height="8.2" fill="#00a4ef" />
    <rect x="9" y="9" width="8.2" height="8.2" fill="#ffb900" />
  </svg>
);

const CardIcon: React.FC<{ size?: number; opacity?: number }> = ({
  size = 44,
  opacity = 1,
}) => (
  <svg width={size} height={size} viewBox="0 0 44 44" fill="none" opacity={opacity}>
    <rect x="4" y="8" width="36" height="28" rx="4" stroke="white" strokeWidth="1.5" />
    <circle cx="16" cy="20" r="4" stroke="white" strokeWidth="1.2" />
    <line x1="26" y1="17" x2="36" y2="17" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="26" y1="22" x2="33" y2="22" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="10" y1="30" x2="34" y2="30" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const QRIcon: React.FC<{ size?: number; opacity?: number }> = ({
  size = 36,
  opacity = 1,
}) => (
  <svg width={size} height={size} viewBox="0 0 36 36" fill="none" opacity={opacity}>
    <rect x="4" y="4" width="10" height="10" rx="2" stroke="white" strokeWidth="1.2" />
    <rect x="22" y="4" width="10" height="10" rx="2" stroke="white" strokeWidth="1.2" />
    <rect x="4" y="22" width="10" height="10" rx="2" stroke="white" strokeWidth="1.2" />
    <rect x="7" y="7" width="4" height="4" rx="1" fill="white" />
    <rect x="25" y="7" width="4" height="4" rx="1" fill="white" />
    <rect x="7" y="25" width="4" height="4" rx="1" fill="white" />
    <rect x="22" y="22" width="3" height="3" fill="white" />
    <rect x="28" y="22" width="4" height="3" fill="white" />
    <rect x="22" y="28" width="3" height="4" fill="white" />
    <rect x="28" y="29" width="4" height="3" fill="white" />
  </svg>
);

const BusinessCardLogo: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = "white" }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <rect x="1" y="1" width="5" height="5" rx="1.5" fill={color} />
    <rect x="8" y="1" width="5" height="5" rx="1.5" fill={color} />
    <rect x="1" y="8" width="5" height="5" rx="1.5" fill={color} />
    <rect x="8" y="8" width="5" height="5" rx="1.5" fill={color} opacity={0.3} />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Feature pill                                                       */
/* ------------------------------------------------------------------ */

const FeaturePill: React.FC<{ icon: React.ReactNode; label: string; delay: string }> = ({
  icon,
  label,
  delay,
}) => (
  <HStack
    spacing={2.5}
    bg="whiteAlpha.100"
    backdropFilter="blur(12px)"
    border="1px solid"
    borderColor="whiteAlpha.150"
    borderRadius="full"
    px={4}
    py={2}
    animation={`${fadeUp} 0.6s ${delay} both`}
  >
    {icon}
    <Text fontSize="12px" fontWeight={500} color="whiteAlpha.900" letterSpacing="0.02em">
      {label}
    </Text>
  </HStack>
);

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const Login: React.FC = () => {
  const navigate = useNavigate();
  const toast = useAppToast();
  const { login, logout, user, isAuthenticated, isLoading, tenantMismatch, clearTenantMismatch } = useAuth();
  const { company, isEKEDP } = useCompany();
  const [signingIn, setSigningIn] = useState(false);

  React.useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'Admin' || user.role === 'Supervisor') {
        navigate('/supervisor');
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleLogin = async () => {
    setSigningIn(true);
    try {
      await login();
      toast({
        title: 'Signed in successfully',
        description: 'Welcome back — redirecting you now.',
        status: 'success',
        duration: 3000,
      });
    } catch {
      toast({
        title: 'Sign-in failed',
        description: 'Something went wrong. Please try again.',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSigningIn(false);
    }
  };

  const rightPanelBg = useColorModeValue('#FFFFFF', '#091021');
  const badgeTitleColor = useColorModeValue('#0F172A', '#F8FAFC');
  const badgeSubColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('#0B132B', '#F8FAFC');
  const descColor = useColorModeValue('gray.600', 'gray.400');
  const dividerBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const ssoSubColor = useColorModeValue('gray.500', 'gray.400');
  const shieldColor = useColorModeValue('#4A5568', '#94A3B8');
  const shieldSubColor = useColorModeValue('gray.600', 'gray.400');
  const footerBorder = useColorModeValue('gray.100', 'whiteAlpha.100');
  const footerTextColor = useColorModeValue('gray.500', 'gray.500');

  if (isLoading) {
    return (
      <Center minH="100vh" bg={NAVY}>
        <Spinner size="lg" color="white" thickness="3px" />
      </Center>
    );
  }

  return (
    <Flex
      h="100vh"
      direction={{ base: 'column', lg: 'row' }}
      fontFamily="'DM Sans', sans-serif"
      overflow="hidden"
    >
      {/* ============================================================= */}
      {/*  LEFT  — Immersive brand panel                                 */}
      {/* ============================================================= */}
      <Box
        flex={{ base: 'none', lg: 1.15 }}
        h={{ base: '320px', lg: 'auto' }}
        bg={NAVY}
        position="relative"
        overflow="hidden"
        display={{ base: 'none', lg: 'flex' }}
        flexDirection="column"
        justifyContent="space-between"
      >
        {/* Gradient overlay */}
        <Box
          position="absolute"
          inset={0}
          bgGradient={isEKEDP ? "linear(135deg, #13102C 0%, #201D45 40%, #312E60 100%)" : `linear(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 40%, #1A3580 100%)`}
          opacity={0.97}
        />

        {/* Decorative grid pattern */}
        <Box
          position="absolute"
          inset={0}
          opacity={0.03}
          backgroundImage="linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)"
          backgroundSize="48px 48px"
          pointerEvents="none"
        />

        {/* Floating decorative elements */}
        <Box
          position="absolute"
          top="12%"
          right="10%"
          animation={`${float} 7s ease-in-out infinite`}
          opacity={0.12}
        >
          <CardIcon size={160} />
        </Box>
        <Box
          position="absolute"
          bottom="18%"
          left="8%"
          animation={`${float} 9s ease-in-out infinite 1s`}
          opacity={0.08}
        >
          <QRIcon size={120} />
        </Box>

        {/* Glowing orb */}
        <Box
          position="absolute"
          top="25%"
          left="50%"
          transform="translate(-50%, -50%)"
          w="400px"
          h="400px"
          borderRadius="full"
          bg={`radial-gradient(circle, ${company.colors.heroGlow} 0%, transparent 70%)`}
          filter="blur(60px)"
          pointerEvents="none"
        />

        {/* Content */}
        <Flex
          direction="column"
          justify="center"
          flex={1}
          position="relative"
          zIndex={1}
          px={{ base: 8, md: 14, xl: 20 }}
          py={{ base: 8, lg: 10 }}
        >
            <Flex align="center" justify="flex-start" mb={{ base: 6, lg: 10 }}>
              <ChakraImage 
                src={company.logo} 
                h={{ base: "44px", lg: "52px" }} 
                objectFit="contain" 
                alt={`${company.companyName} Logo`}
              />
            </Flex>

          {/* Headline */}
          <Box animation={`${fadeUp} 0.7s 0.1s both`}>
            <Text
              fontFamily="'Cormorant Garamond', serif"
              fontSize={{ base: '36px', md: '52px', xl: '60px' }}
              fontWeight={300}
              lineHeight={1.05}
              color="white"
              letterSpacing="-1px"
              mb={4}
            >
              Your Professional
              <br />
              Identity,{' '}
              <Box
                as="span"
                bgGradient="linear(90deg, #8CB4FF, #BDD4FF, #8CB4FF)"
                bgClip="text"
                backgroundSize="200% auto"
                animation={`${shimmer} 4s linear infinite`}
              >
                Digitised.
              </Box>
            </Text>
            <Text
              fontSize={{ base: '14px', md: '15px' }}
              fontWeight={400}
              color="white"
              lineHeight={1.7}
              maxW="420px"
            >
              Create, manage and share your digital business card — complete with QR codes, vCard exports and supervisor approval workflows.
            </Text>
          </Box>

          {/* Feature pills */}
          <HStack
            spacing={3}
            mt={{ base: 4, lg: 7 }}
            flexWrap="wrap"
            display={{ base: 'none', md: 'flex' }}
          >
            <FeaturePill
              delay="0.3s"
              icon={<CardIcon size={16} opacity={0.8} />}
              label="Digital Business Cards"
            />
            <FeaturePill
              delay="0.45s"
              icon={<QRIcon size={16} opacity={0.8} />}
              label="QR Code Generation"
            />
            <FeaturePill
              delay="0.6s"
              icon={
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" opacity={0.8}>
                  <path d="M8 2L10 6L14 6.5L11 9.5L12 14L8 12L4 14L5 9.5L2 6.5L6 6L8 2Z" stroke="white" strokeWidth="1.2" strokeLinejoin="round" />
                </svg>
              }
              label="Approval Workflow"
            />
          </HStack>
        </Flex>

        {/* Bottom bar */}
        <HStack
          position="relative"
          zIndex={1}
          px={{ base: 8, md: 14, xl: 20 }}
          pb={6}
          spacing={5}
          display={{ base: 'none', lg: 'flex' }}
        >
          <Text fontSize="11px" color="whiteAlpha.800" letterSpacing="0.02em">
            {company.copyright}
          </Text>
          <Box w="1px" h="12px" bg="whiteAlpha.400" />
          <Text fontSize="11px" color="whiteAlpha.800" letterSpacing="0.02em">
            {company.itSupportText}
          </Text>
        </HStack>
      </Box>

      {/* ============================================================= */}
      {/*  RIGHT — Login form                                            */}
      {/* ============================================================= */}
      <Flex
        flex={{ base: 1, lg: 0.85 }}
        bg={rightPanelBg}
        align="center"
        justify="center"
        position="relative"
        overflow="hidden"
        px={{ base: 6, md: 12, lg: 16 }}
        py={{ base: 8, lg: 0 }}
        transition="background 0.25s ease"
      >
        {/* Floating Theme Toggle on Top Right */}
        <Box position="absolute" top={{ base: 4, md: 6 }} right={{ base: 4, md: 6 }} zIndex={10}>
          <ThemeToggle variant="segmented" size="sm" />
        </Box>

        <Box w="full" maxW="380px" animation={`${fadeUp} 0.5s 0.15s both`}>

          {/* Card-like badge */}
          <HStack spacing={3} mb={7}>
            <Flex
              w="42px"
              h="42px"
              bg={company.colors.buttonGradient}
              borderRadius="12px"
              align="center"
              justify="center"
              flexShrink={0}
              boxShadow="0 4px 14px rgba(9, 26, 66, 0.25)"
            >
              <BusinessCardLogo size={20} />
            </Flex>
            <Box>
              <Text fontSize="15px" fontWeight={800} color={badgeTitleColor} letterSpacing="-0.02em">
                {company.shortName} ID
              </Text>
              <Text fontSize="12px" color={badgeSubColor} fontWeight={600} letterSpacing="0.04em">
                {company.tagline}
              </Text>
            </Box>
          </HStack>

          {/* Heading */}
          <Text
            as="h1"
            fontFamily="'Cormorant Garamond', Georgia, serif"
            fontSize={{ base: '38px', md: '46px' }}
            fontWeight={400}
            lineHeight={1.05}
            color={headingColor}
            mb={2}
            letterSpacing="-0.5px"
          >
            Welcome back.
          </Text>
          <Text
            fontSize="14px"
            fontWeight={400}
            color={descColor}
            lineHeight={1.4}
            mb={8}
          >
            Sign in with your {company.companyName} domain account.
          </Text>

          {/* Divider */}
          <HStack spacing={4} mb={6} w="full">
            <Divider borderColor={dividerBorder} />
            <Text
              fontSize="11px"
              color={ssoSubColor}
              fontWeight={700}
              letterSpacing="0.08em"
              textTransform="uppercase"
              whiteSpace="nowrap"
            >
              Single Sign-On
            </Text>
            <Divider borderColor={dividerBorder} />
          </HStack>

          {/* SSO Button */}
          <VStack spacing={3} w="full">
            <Button
              w="full"
              h="54px"
              bg={company.colors.buttonGradient}
              color="white"
              borderRadius="12px"
              fontSize="14px"
              fontWeight={600}
              letterSpacing="0.02em"
              leftIcon={<MicrosoftIcon />}
              boxShadow="0 4px 16px rgba(16, 66, 157, 0.25)"
              _hover={{
                bg: company.colors.buttonHoverGradient,
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(16, 66, 157, 0.35)',
              }}
              _active={{ transform: 'translateY(0)' }}
              isLoading={signingIn}
              loadingText="Authenticating…"
              onClick={handleLogin}
              transition="all 0.2s cubic-bezier(0.16, 1, 0.3, 1)"
            >
              Sign in with Microsoft
            </Button>

            {/* Security note */}
            <HStack spacing={2} mt={2}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M6 1L2 3V5.5C2 8.15 3.71 10.59 6 11.25C8.29 10.59 10 8.15 10 5.5V3L6 1Z"
                  stroke={shieldColor}
                  strokeWidth="1"
                  strokeLinejoin="round"
                />
                <path d="M4.5 6L5.5 7L7.5 5" stroke={shieldColor} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <Text fontSize="11px" color={shieldSubColor} fontWeight={600}>
                Secured by Microsoft Entra ID
              </Text>
            </HStack>
          </VStack>

          {/* Footer */}
          <Box mt={7} pt={5} borderTop="1px solid" borderColor={footerBorder}>
            <Text
              fontSize="11px"
              color={footerTextColor}
              lineHeight={1.8}
              textAlign="center"
              letterSpacing="0.01em"
            >
              {company.termsText}
              <br />
              and acknowledge the Privacy Policy.
            </Text>
          </Box>
        </Box>
      </Flex>

      {/* Security Policy Modal: Kicks out and informs cross-tenant users */}
      <TenantAccessDeniedModal
        data={tenantMismatch}
        onClose={clearTenantMismatch}
        onSignOut={async () => {
          const account = tenantMismatch?.account;
          clearTenantMismatch();
          try {
            await logout(account);
          } catch (e) {
            console.error("Sign-out on modal dismiss error:", e);
          }
        }}
      />
    </Flex>
  );
};

export default Login;